import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface Lead {
  id: string;
  first_name: string;
  email: string;
  device_type: string;
  referrer: string | null;
  created_at: string;
}

interface PageView {
  id: string;
  device_type: string;
  created_at: string;
}

export default function AdminLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [pageViews, setPageViews] = useState<PageView[]>([]);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');

  // Simple password gate — change this to your admin password
  const ADMIN_PASSWORD = 'dkmba2024';

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthed(true);
      sessionStorage.setItem('admin_authed', 'true');
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('admin_authed') === 'true') {
      setAuthed(true);
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    async function fetchData() {
      setLoading(true);
      const [leadsRes, viewsRes] = await Promise.all([
        supabase.from('free_plan_leads').select('*').order('created_at', { ascending: false }),
        supabase.from('free_plan_page_views').select('*').order('created_at', { ascending: false }),
      ]);
      if (leadsRes.data) setLeads(leadsRes.data);
      if (viewsRes.data) setPageViews(viewsRes.data);
      setLoading(false);
    }
    fetchData();
  }, [authed]);

  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const todayLeads = leads.filter(l => l.created_at.slice(0, 10) === today).length;
    const todayViews = pageViews.filter(v => v.created_at.slice(0, 10) === today).length;
    const totalLeads = leads.length;
    const totalViews = pageViews.length;
    const conversionRate = totalViews > 0 ? ((totalLeads / totalViews) * 100).toFixed(1) : '0';
    const todayConversion = todayViews > 0 ? ((todayLeads / todayViews) * 100).toFixed(1) : '0';
    const mobileLeads = leads.filter(l => l.device_type === 'mobile').length;
    const desktopLeads = leads.filter(l => l.device_type === 'desktop').length;
    const mobileViews = pageViews.filter(v => v.device_type === 'mobile').length;
    const desktopViews = pageViews.filter(v => v.device_type === 'desktop').length;
    return { todayLeads, todayViews, totalLeads, totalViews, conversionRate, todayConversion, mobileLeads, desktopLeads, mobileViews, desktopViews };
  }, [leads, pageViews]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <form onSubmit={handleLogin} className="bg-white rounded-xl shadow-lg p-8 w-full max-w-sm space-y-4">
          <h1 className="text-xl font-bold text-center">Admin Access</h1>
          <input
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="w-full h-12 px-4 rounded-lg border border-gray-300 text-base focus:outline-none focus:ring-2 focus:ring-black"
          />
          <button type="submit" className="w-full h-12 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-colors">
            Login
          </button>
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Free Plan — Lead Analytics</h1>
          <button
            onClick={() => { sessionStorage.removeItem('admin_authed'); setAuthed(false); }}
            className="text-sm text-gray-500 hover:text-black"
          >
            Logout
          </button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Leads" value={stats.totalLeads} />
          <StatCard label="Today's Leads" value={stats.todayLeads} />
          <StatCard label="Total Page Views" value={stats.totalViews} />
          <StatCard label="Today's Views" value={stats.todayViews} />
          <StatCard label="Conversion Rate" value={`${stats.conversionRate}%`} />
          <StatCard label="Today's Conversion" value={`${stats.todayConversion}%`} />
          <StatCard label="Mobile Leads" value={stats.mobileLeads} sub={`${stats.mobileViews} views`} />
          <StatCard label="Desktop Leads" value={stats.desktopLeads} sub={`${stats.desktopViews} views`} />
        </div>

        {/* Leads table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="p-4 border-b">
            <h2 className="font-semibold">Recent Leads ({leads.length})</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Device</TableHead>
                <TableHead>Referrer</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.slice(0, 100).map(lead => (
                <TableRow key={lead.id}>
                  <TableCell className="text-sm text-gray-600">
                    {new Date(lead.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </TableCell>
                  <TableCell className="font-medium">{lead.first_name}</TableCell>
                  <TableCell>{lead.email}</TableCell>
                  <TableCell>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${lead.device_type === 'mobile' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                      {lead.device_type}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">{lead.referrer || '—'}</TableCell>
                </TableRow>
              ))}
              {leads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-400 py-8">No leads yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-5">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}
